import { reportingClient } from "./httpClients/reportingClient";

async function parseEnvelope(response) {
  const data = response?.data ?? response;
  if (!data.success) {
    const msg = data.errorInfo?.message || "Request failed";
    const err = new Error(msg);
    err.code = data.errorInfo?.code;
    throw err;
  }
  return data.payload;
}

/** Fetch file as Blob from URL (e.g. from GetUserFiles). Uses Bearer token for auth. */
export async function getFileBlobFromUrl(fileUrl, accessToken) {
  if (!fileUrl) throw new Error("File URL is required");
  // Use axios to download blob; auth is attached centrally.
  const response = await reportingClient.get(fileUrl, { responseType: "blob" });
  return response.data;
}

/**
 * Upload referral file(s) in one request. One or multiple files sent as "Files".
 * Server returns a single fileId (combined when multiple). Use that fileId for one AnalyzeReferral call.
 */
export async function uploadReferralFiles(files, accessToken) {
  const list = Array.isArray(files) ? files : [files].filter(Boolean);
  if (!list.length) throw new Error("At least one file is required");
  const formData = new FormData();
  for (const file of list) {
    formData.append("Files", file);
  }
  // IMPORTANT: Do not set Content-Type manually for FormData; browser will set boundary.
  const response = await reportingClient.post(
    "/api/Protocol/UploadReferralFile",
    formData
  );
  return parseEnvelope(response);
}

/**
 * 2. Analyze referral: OCR → anatomy → protocols → selected protocol + explanation.
 * Pass language (e.g. en, he, ar, es, fr) same as other endpoints (GetTemplatesMulty, etc.).
 * @param {{ fileId: string, model?: string, language?: string }} body
 */
export async function analyzeReferral(body, accessToken) {
  const payload = {
    fileId: body.fileId,
    model: body.model || "medilis.ai.V1(faster)",
  };
  if (body.language) payload.language = body.language;
  const response = await reportingClient.post("/api/Protocol/AnalyzeReferral", payload);
  return parseEnvelope(response);
}

/** OCR only — extracted text, no AI steps */
export async function extractReferralText(body, accessToken) {
  const response = await reportingClient.post("/api/Protocol/ExtractReferralText", {
    fileId: body.fileId,
  });
  return parseEnvelope(response);
}

/** List of anatomies — call once on startup, cache for dropdowns */
export async function getAnatomies(accessToken) {
  const response = await reportingClient.post("/api/Protocol/GetAnatomies", {});
  return parseEnvelope(response);
}

/** All protocols, optionally filtered by anathomy */
export async function getProtocols(params, accessToken) {
  const response = await reportingClient.post("/api/Protocol/GetProtocols", params || {});
  return parseEnvelope(response);
}
